import Dropzone, { useDropzone } from 'react-dropzone';
import { Eye, MagnifyingGlass, Pencil, Trash } from '@phosphor-icons/react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from 'src/components/Button';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { CopyableField } from 'src/components/CopyableField';
import { Heading } from 'src/components/Heading';
import { Input } from 'src/components/Input';
import { Loading } from 'src/components/Loading';
import { Modal } from 'src/components/Modal';
import { SideDropActions } from 'src/components/SideDropActions';
import Table from 'src/components/Table';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { can, Can, Subject } from 'src/hooks/usePermissions';
import {
  KnowledgeBaseFile,
  KnowledgeBasePayload,
} from 'src/interfaces/knowledge-base.interface';
import { StabilityLevel } from 'src/types/stability-level';

type KnowledgeBaseProps = {};

const headers = [
  { key: 'id', label: 'ID', width: '8rem' },
  { key: 'label', label: 'Name', width: '60%' },
  { key: 'action', label: '', width: '20%', disableSorting: true },
];

export const ManageKnowledgeBase = (props: KnowledgeBaseProps) => {
  const { get, patch, post, remove } = useAuthedRequest();

  const navigate = useNavigate();
  const { role } = useEnvironment();
  const { id } = useParams();

  const notification = useNotification();

  const [loading, setLoading] = useState(false);
  const [knowledgeBase, setKnowledgeBase] = useState<KnowledgeBasePayload>({
    label: '',
    description: '',
  });

  const [rows, setRows] = useState<KnowledgeBaseFile[]>([]);
  const [modalDeleteOpen, setModalDeleteOpen] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [size, setSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    acceptedFiles.forEach(async (file: File) => {
      const reader = new FileReader();

      reader.onabort = () =>
        notification.error('File reading was aborted. Please try again.');
      reader.onerror = () =>
        notification.error('File reading has failed. Please try again.');
      reader.onload = async () => {
        // Do whatever you want with the file contents
        const binaryStr = reader.result;
        if (!binaryStr) return;
        const formData = new FormData();
        formData.append(
          'file',
          new Blob([binaryStr], { type: file.type }),
          file.name,
        );
        setLoading(true);

        await post(`/knowledge-bases/${id}/files`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        await fetchData();
        setLoading(false);
      };
      reader.readAsArrayBuffer(file);
    });
  }, []);
  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const fetchData = async () => {
    setLoading(true);
    const knowledgeBase = await get(`/knowledge-bases/${id}`);
    setKnowledgeBase(knowledgeBase);
    const data = await get(`/knowledge-bases/${id}/files`);
    const files = data.items.map((item: KnowledgeBaseFile) => ({
      id: <CopyableField value={item.id} notification={notification} />,
      label: item.label,
      action: (
        <div
          className="flex justify-center items-center"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <SideDropActions
            options={[
              {
                label: 'Delete',
                Icon: Trash,
                color: 'error' as const,
                show: can(role, 'delete', Subject.KNOWLEDGE_BASES),
                onClick: () => openDeleteKnowledgeBaseFile(item),
              },
            ]}
          />
        </div>
      ),
    }));

    setRows(files);
    setTotalItems(files.length);
    setLoading(false);
  };

  const handlePageChange = (page: number, perPage: number) => {
    setCurrentPage(page);
    setSize(perPage);
  };

  const handleSizeChange = (perPage: number) => {
    setSize(perPage);
    setCurrentPage(1);
  };

  const openDeleteKnowledgeBaseFile = (
    knowledgeBaseFile: KnowledgeBaseFile,
  ) => {
    setModalDeleteOpen(true);
    setSelectedId(knowledgeBaseFile.id);
  };

  const handleRemoveKnowledgeBaseFile = async () => {
    setLoading(true);
    try {
      await remove(`/knowledge-bases/${id}/files/${selectedId}`);
      fetchData();
      setModalDeleteOpen(false);
      notification.success('File deleted successfully');
    } catch {
      notification.error('Failed to delete file');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id && !loading) {
      fetchData();
    }
  }, [id]);

  return (
    <div className="flex-1">
      <Heading
        title={id ? `Files in ${knowledgeBase.label}` : 'New Knowledge Base'}
        stabilityLevel={StabilityLevel.Beta}
      />

      {loading ? (
        <div className="flex justify-center items-center h-full">
          <Loading size="8" className="mt-8" />
        </div>
      ) : (
        <>
          <ContentWrapper>
            <Can operation="create" subject={Subject.KNOWLEDGE_BASES}>
              <section className="bg-neutral-100 p-6 rounded-lg my-6 border border-neutral-200 border-dashed cursor-pointer">
                <div
                  {...getRootProps()}
                  className="flex items-center justify-center"
                >
                  <input {...getInputProps()} />
                  <p>
                    Drag 'n' drop some files here, or click to select files to
                    upload
                  </p>
                </div>
                <div className="flex items-center justify-center text-sm ">
                  Supported file types: PDF, TXT, RTF, DOC, DOCX, PPT, PPTX
                </div>
              </section>
            </Can>

            <Can operation="read" subject={Subject.KNOWLEDGE_BASES}>
              <Table
                headers={headers}
                rows={rows}
                totalItems={totalItems}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                onSort={() => {}}
                loading={loading}
                setSize={handleSizeChange}
                defaultSize={size}
                onRowClick={(index: number) => {
                  const knowledgeBase = rows[index];
                  if (knowledgeBase) {
                    navigate(
                      `/knowledge-bases/${(knowledgeBase.id as unknown as { props: { value: string } }).props.value}`,
                    );
                  }
                }}
              />
            </Can>
          </ContentWrapper>

          <Modal
            title="Delete File"
            isOpen={modalDeleteOpen}
            onClose={() => {
              setModalDeleteOpen(false);
              setSelectedId('');
            }}
            actionButton={
              <Button onClick={handleRemoveKnowledgeBaseFile}>
                Delete File
              </Button>
            }
            className="w-96"
          >
            <div className="text-center">
              Are you sure you want to delete this file?
            </div>
          </Modal>
        </>
      )}
    </div>
  );
};
